import React, { useCallback } from 'react';
import { arrayOf, shape, number, string, object, bool } from 'prop-types';
import getConfig from 'next/config';
import Script from '@lmig/dotcom-aspect-components/Utility/Script';
import ReactDOMServer from "react-dom/server";
import { Link, IconLocations, IconPhone } from '@lmig/lmds-react';

const {
  publicRuntimeConfig: { googleMapUrl, googleApiKey: GOOG_API_KEY }
} = getConfig();

const googleApiJs = `${googleMapUrl}?key=${GOOG_API_KEY}&v=3.exp&libraries=geometry,drawing&loading=async&callback=initGoogleMap`;

const MarkerInfoWindow = ({ marker }) => (<div style={{ minWidth: '10rem' }}>
  <a href="#" style={{ display: 'none' }}>a</a>
  <Link href={`/${marker.alias}`} variant="standalone"><span style={{ fontSize: "1rem", fontWeight: "500" }}>{marker.name}</span></Link>
  <div style={{ marginTop: '0.5rem' }}>{marker.address.street}</div>
  {marker.address.additionalStreetInfo && <div style={{ marginTop: '0.5rem' }}>{marker.address.additionalStreetInfo}</div>}
  <div style={{ marginTop: '0.5rem' }}>{marker.address.city}, {marker.address.state.code} {marker.address.zip}</div>
  <div style={{ borderTop: '1px solid #E6E6E6', marginTop: '0.25rem', paddingTop: '0.5rem', display: 'flex' }}>
    <Link href={`https://www.google.com/maps/dir//Comparion+Insurance+Agency+${marker.address.street}`} variant="standalone" caretPosition="none" aria-label={`Get directions to the ${marker.address?.city} office`} target="_blank">
      <IconLocations size="16" />Get directions
    </Link>
    {marker.phone && <div style={{ marginLeft: '1rem' }}>
      <Link href={`tel:${marker.phone}`} variant="standalone" caretPosition="none" aria-label="Contact us" target="_blank">
        <IconPhone size="16" />Call us
      </Link>
    </div>}
  </div>
</div>);

const GoogleMaps = ({
  markers = [],
  height = '800px',
  stateName,
  useCustomMarker = true
}) => {
  let map;
  let geocoder;

  if (typeof window !== "undefined") {
    window.initGoogleMap = async () => {
      const { Map, InfoWindow } = await window.google.maps.importLibrary("maps");
      const { AdvancedMarkerElement, PinElement } = await window.google.maps.importLibrary(
        "marker",
      );
      // Create an info window to share between markers.
      const infoWindow = new InfoWindow();

      const createMarker = (recordMarker, markerIndex) => {
        const lat = parseFloat(recordMarker.lat);
        const lng = parseFloat(recordMarker.lng);
        if (!lat || !lng)
          return;

        let markerData;

        if (useCustomMarker) {
          const pin = new PinElement({
            glyph: String.fromCharCode(markerIndex + 65),
            background: '#1A1446',
            borderColor: '#1A1446',
            glyphColor: "white",
          });
          markerData = {
            map,
            position: { lat, lng },
            title: String.fromCharCode(markerIndex + 65),
            content: pin.element,
          };
        } else {
          markerData = {
            map,
            position: { lat, lng },
          };
        }

        const marker = new AdvancedMarkerElement(markerData);

        if (useCustomMarker) {
          marker.addListener("click", () => {
            infoWindow.close();
            infoWindow.setContent(ReactDOMServer.renderToString(<MarkerInfoWindow marker={recordMarker} />));
            infoWindow.open(marker.map, marker);
          });
        }

        // eslint-disable-next-line consistent-return
        return marker;
      };

      const styledMapType = new window.google.maps.StyledMapType([
        {
          "featureType": "poi",
          "elementType": "labels.text",
          "stylers": [
            {
              "visibility": "off"
            }
          ]
        },
        {
          "featureType": "poi.business",
          "stylers": [
            {
              "visibility": "off"
            }
          ]
        },
        {
          "featureType": "road",
          "elementType": "labels.icon",
          "stylers": [
            {
              "visibility": "off"
            }
          ]
        },
        {
          "featureType": "transit",
          "stylers": [
            {
              "visibility": "off"
            }
          ]
        }
      ]);

      map = new Map(document.getElementById("map"), {
        zoom: 15,
        maxZoom: 15,
        center: { lat: 39.8, lng: -98.5 },
        mapId: 'map',
      });
      map.mapTypes.set("styled_map", styledMapType);
      map.setMapTypeId("styled_map");

      const markersPos = [];
      const markersObj = {};
      const bounds = new window.google.maps.LatLngBounds();

      markers.forEach((marker, index) => {
        const resMarker = createMarker(marker, index);

        if (resMarker) {
          const pos = resMarker.position;

          if (pos) {
            markersPos.push(pos);
            markersObj[marker.alias] = marker;
          }
        }
      });

      if (stateName) {
        geocoder = new window.google.maps.Geocoder();

        // Convert the state name into coordinates
        geocoder.geocode({ address: `${stateName} State, USA` }, (results, status) => {
          if (status === "OK") {
            if (results[0]) {
              map.fitBounds(results[0].geometry.viewport);
            }
          }
        });
      }
      else {
        for (let i = 0; i < markersPos.length; i += 1) {
          bounds.extend(markersPos[i]);
        }

        if (map) {
          map.fitBounds(bounds);
        }
      }
    };
  }

  const loadMap = useCallback(() => {
    if (typeof window !== "undefined" && window.google?.maps?.importLibrary) {
      window.initGoogleMap();
    }
  }, []);

  return (
    <>
      <div id="map" style={{ width: '100%', height: `${height}` }} />
      <Script url={googleApiJs} defer>{loadMap()}</Script>
    </>);
};

GoogleMaps.propTypes = {
  markers: arrayOf(
    shape({
      lat: number,
      lng: number,
      name: string,
      phone: string,
      address: object,
      alias: string,
    }),
  ),
  height: string,
  stateName: string,
  useCustomMarker: bool,
};

export default GoogleMaps;